<template>
  <teleport to="body" :disabled="!isMobile">
    <div class="sidebar" :class="{ collapsed: isCollapsed }">
      <div class="sidebar-header">
        <!-- Mobile Close Button -->
        <div v-if="isMobile" class="mobile-close-btn" @click="toggleSidebar">
          <i class="bx bx-x"></i>
        </div>

        <!-- Logo Area -->
        <div class="logo-container text-center" @click="goToHome">
          <transition name="fade" mode="out-in">
            <img
              v-if="!isCollapsed"
              :key="'new'"
              src="../assets/ctrlytics-logo-black.png"
              alt="Logo"
              class="full-logo"
            />
            <img
              v-else
              :key="'old'"
              src="../assets/ctrlytics-icon.png"
              alt="Logo"
              class="icon-logo"
            />
          </transition>
        </div>
      </div>

      <div class="sidebar-content">
        <div class="menu-section">
          <!-- Dashboard Item (Moved to Top) -->
          <div
            class="menu-item"
            :class="{ active: isActive('/dashboard') }"
            @click="goToPage('/dashboard')"
            :title="dashboardLabel"
          >
            <i class="bx bx-home-alt icon-image"></i>
            <span class="image-label" v-show="!isCollapsed">{{
              dashboardLabel
            }}</span>
          </div>

          <!-- Top Items (Dynamic) -->
          <div
            v-for="item in imageItemsTop"
            :key="item.id"
            class="menu-item"
            :class="{ active: isActive(item.route) }"
            @click="goToPage(item.route)"
            :title="item.altText"
          >
            <i :class="['bx', item.iconClass, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{
              item.altText
            }}</span>
          </div>

          <!-- Manage Device Items -->
          <div
            v-for="item in imageItemsManageDevice"
            :key="item.id"
            class="menu-item"
            :class="{ active: isActive(item.route) }"
            @click="goToPage(item.route)"
            :title="item.altText"
          >
            <i :class="['bx', item.iconClass, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{
              item.altText
            }}</span>
          </div>

          <!-- Mid Items -->
          <div
            v-for="item in imageItemsMid"
            :key="item.id"
            class="menu-item"
            :class="{ active: isActive(item.route) }"
            @click="goToPage(item.route)"
            :title="item.altText"
          >
            <i :class="['bx', item.iconClass, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{
              item.altText
            }}</span>
          </div>
        </div>

        <!-- Footer Items -->
        <div class="sidebar-footer">
          <!-- ABM Connection (Discreet Footer Link) -->
          <div
            v-if="data_collection.includes('ABM Connection')"
            class="menu-item abm-footer-item"
            :class="{ active: isActive('/abm-connection') }"
            @click="goToPage('/abm-connection')"
            title="ABM Connection"
          >
            <i class="bx bx-link-external icon-image"></i>
            <span class="image-label" v-show="!isCollapsed">ABM Connection</span>
          </div>

          <!-- LOCAL-ONLY simulation footer links (e.g. Voldi Connection test).
               Registered from src/sim/ which is gitignored; absent in staging/production. -->
          <div
            v-for="item in simFooterItems"
            :key="item.route"
            class="menu-item abm-footer-item"
            :class="{ active: isActive(item.route) }"
            @click="goToPage(item.route)"
            :title="item.label"
          >
            <i :class="['bx', item.icon, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{ item.label }}</span>
          </div>

          <!-- Language Dropdown Item -->
          <div
            class="menu-item lang-menu-item"
            @click.stop="toggleLangMenu"
            title="Language"
          >
            <img :src="currentLangFlag" alt="Language" class="icon-image" />
            <span class="image-label" v-show="!isCollapsed">{{
              currentLangLabel
            }}</span>
            <!-- Dropdown Arrow -->
            <span
              v-show="!isCollapsed"
              class="dropdown-arrow"
              :class="{ rotated: showLangMenu }"
              >▼</span
            >

            <!-- Dropdown Content -->
            <transition name="fade">
              <div v-if="showLangMenu" class="lang-dropdown">
                <div
                  class="lang-option"
                  @click.stop="selectLang('ENG')"
                  title="English"
                >
                  <img
                    src="../assets/country_flags/en_US.svg"
                    class="flag-mini"
                  />
                  <span v-show="!isCollapsed">English</span>
                </div>
                <div
                  class="lang-option"
                  @click.stop="selectLang('BM')"
                  title="Bahasa Malaysia"
                >
                  <img
                    src="../assets/country_flags/ms_MY.svg"
                    class="flag-mini"
                  />
                  <span v-show="!isCollapsed">Bahasa Malaysia</span>
                </div>
                <div
                  class="lang-option"
                  @click.stop="selectLang('th')"
                  title="Thai"
                >
                  <img
                    src="../assets/country_flags/th_TH.svg"
                    class="flag-mini"
                  />
                  <span v-show="!isCollapsed">Thai</span>
                </div>
              </div>
            </transition>
          </div>

          <!-- Log Out Item -->
          <div
            class="menu-item logout-item"
            @click="showLogoutConfirmation"
            :title="logoutLabel"
          >
            <i class="bx bx-log-out-circle icon-image"></i>
            <span class="image-label" v-show="!isCollapsed">{{
              logoutLabel
            }}</span>
          </div>
        </div>
      </div>

      <!-- Logout Confirmation Modal -->
      <teleport to="body">
        <div v-if="showLogoutModal">
          <div class="overlay"></div>
          <div class="premium_modal">
            <div class="modal_content">
              <div class="modal_icon_wrapper">
                <img
                  src="../assets/warning.png"
                  class="modal_icon"
                  alt="Warning"
                />
              </div>
              <h3 class="modal_title">
                {{
                  lang.logout_modal ? lang.logout_modal.title : "Confirm Logout"
                }}
              </h3>
              <p class="modal_message">
                {{
                  lang.logout_modal
                    ? lang.logout_modal.message
                    : "Are you sure you want to log out?"
                }}
              </p>
              <div class="modal_actions">
                <button class="btn_primary_large" @click="confirmLogout">
                  {{
                    lang.logout_modal
                      ? lang.logout_modal.confirm_button
                      : "Yes, Log Out"
                  }}
                </button>
                <button class="btn_secondary_large" @click="cancelLogout">
                  {{
                    lang.logout_modal
                      ? lang.logout_modal.cancel_button
                      : "Cancel"
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </teleport>

      <!-- Mobile Backdrop -->
      <teleport to="body">
        <transition name="fade">
          <div
            class="sidebar-backdrop"
            v-if="!isCollapsed && isMobile"
            @click="toggleSidebar"
          ></div>
        </transition>
      </teleport>
    </div>
  </teleport>
</template>

<script>
import { endSession } from "../api/sessionGuard";

export default {
  emits: ["button-click"],
  data() {
    return {
      isCollapsed: JSON.parse(
        localStorage.getItem("sidebarCollapsed") || "false",
      ),
      showLangMenu: false,
      // local-only simulation footer links (gitignored sim folder; empty in production)
      simFooterItems: (typeof window !== "undefined" && window.__CTRLYTICS_SIM_FOOTER__) || [],
      imageItemsTop: [],
      imageItemsManageDevice: [],
      imageItemsMid: [],
      imageItemsLower: [],
      langmode: "ENG",
      data: [],
      data_collection: [],
      isInLocalStorage: false,
      showLogoutModal: false,
      isMobile: window.innerWidth <= 768,
    };
  },
  computed: {
    currentLangFlag() {
      let flag;
      if (this.langmode === "ENG")
        flag = require("../assets/country_flags/en_US.svg");
      else if (this.langmode === "BM")
        flag = require("../assets/country_flags/ms_MY.svg");
      else if (this.langmode === "th")
        flag = require("../assets/country_flags/th_TH.svg");
      else
        flag = require("../assets/country_flags/en_US.svg");
        
      // Handle both string paths and module objects from webpack
      return typeof flag === 'string' ? flag : (flag.default || flag);
    },
    currentLangLabel() {
      return this.getDisplayLang(this.langmode);
    },
    dashboardLabel() {
      if (
        this.data &&
        this.data[this.langmode] &&
        this.data[this.langmode]["lower"]
      ) {
        return this.data[this.langmode]["lower"][0];
      }
      return "Dashboard";
    },
    logoutLabel() {
      if (
        this.data &&
        this.data[this.langmode] &&
        this.data[this.langmode]["lower"]
      ) {
        return this.data[this.langmode]["lower"][1];
      }
      return "Log Out";
    },
    lang() {
      if (this.data && this.data[this.langmode]) {
        return this.data[this.langmode];
      }
      return {};
    },
  },
  watch: {
    $route() {
      // Re-apply saved language when route changes
      if (this.data && Object.keys(this.data).length > 0) {
        const savedLang = localStorage.locale || "eng";
        const langKey =
          savedLang === "eng" || savedLang === "ENG" || savedLang === "english"
            ? "ENG"
            : savedLang === "bm" || savedLang === "BM" || savedLang === "bahasa"
            ? "BM"
            : savedLang === "th" || savedLang === "TH" || savedLang === "thai"
            ? "th"
            : "ENG";
        console.log("[watch $route] Re-applying language:", langKey);
        this.langchange(langKey);
      }
    },
  },
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem("sidebarCollapsed", this.isCollapsed);
      this.showLangMenu = false; // Close menu on toggle
    },
    toggleLangMenu() {
      this.showLangMenu = !this.showLangMenu;
    },
    selectLang(code) {
      this.langchange(code);
      // Save to localStorage in lowercase for consistency
      localStorage.locale = code.toLowerCase();
      this.showLangMenu = false;
    },
    isActive(route) {
      if (route === "langchange" || route === "/") return false;
      return this.$route.path === route;
    },
    getDisplayLang(code) {
      if (code === "ENG") return "ENG";
      if (code === "BM") return "BM";
      if (code === "th") return "THAI";
      return code.toUpperCase();
    },
    goToPage(route) {
      if (route === "langchange") {
        // Handled by toggleLangMenu now
        return;
      } else if (route === "/") {
        const versionMemory = localStorage.getItem("whatsNewLastSeenVersion");
        localStorage.clear();
        if (versionMemory) {
          localStorage.setItem("whatsNewLastSeenVersion", versionMemory);
        }
        console.log("Clearing storage");
        this.$router.replace(route);
      } else {
        this.$router.replace(route);
      }
    },
    goToHome() {
      // Removed auto-expand logic to respect user preference
      // if(this.isCollapsed) this.isCollapsed = false;
      this.$router.replace("/dashboard");
    },
    // iconClicked removed as font icons don't need image swapping
    showLogoutConfirmation() {
      this.showLogoutModal = true;
    },
    confirmLogout() {
      this.showLogoutModal = false;
      // End the session server-side too. Clearing only the browser left the row live, so the
      // user's own next login evicted it and raised a security event that never happened.
      endSession().finally(() => this.goToPage("/"));
    },
    cancelLogout() {
      this.showLogoutModal = false;
    },
    langchange(val) {
      console.log("[SideNavbar] langchange called with:", val);
      this.langmode = val;

      // Only update dynamic text for main menus if data exists
      if (!this.data[this.langmode]) {
        console.log(
          "[SideNavbar] No data for langmode:",
          this.langmode,
          "Available keys:",
          Object.keys(this.data),
        );
        return;
      }

      console.log("[SideNavbar] Language data found for:", this.langmode);
      console.log("[SideNavbar] Menu items before update:", {
        top: this.imageItemsTop.map((i) => i.altText),
        manage: this.imageItemsManageDevice.map((i) => i.altText),
        mid: this.imageItemsMid.map((i) => i.altText),
      });

      // Top section is now the single merged "Register User" tab (formerly "Enroll Device"). The old
      // CMS "top" labels ("Register Device" / "Enroll User") are stale and would clobber it, so we keep
      // the hardcoded altText here. (Re-seed CMS "top" with "Register User" + translations later.)

      if (
        this.data && this.langmode && this.data[this.langmode] &&
        this.data[this.langmode]["manage"] &&
        this.imageItemsManageDevice &&
        this.imageItemsManageDevice.length > 0
      ) {
        if (this.imageItemsManageDevice[0]) {
          const label = this.data[this.langmode]["manage"][0];
          this.imageItemsManageDevice[0].altText = typeof label === 'string' ? label : String(label || "");
        }
      }

      if (this.imageItemsMid && this.data && this.langmode && this.data[this.langmode] && this.data[this.langmode]["mid"]) {
        this.imageItemsMid.forEach((item) => {
          let index = -1;
          if (item.id === 1) index = 0; // Manage Accounts
          else if (item.id === 2) index = 1; // Manage Templates
          else if (item.id === 3) index = 2; // FAQ
          else if (item.id === 5) index = 3; // Audit Trail
          else if (item.id === 6) index = 4; // Reporting

          if (index !== -1 && this.data[this.langmode]["mid"][index]) {
            const label = this.data[this.langmode]["mid"][index];
            item.altText = typeof label === 'string' ? label : String(label || "");
          }
        });
      }

      console.log("[SideNavbar] Menu items after update:", {
        top: this.imageItemsTop ? this.imageItemsTop.map((i) => i.altText) : [],
        manage: this.imageItemsManageDevice ? this.imageItemsManageDevice.map((i) => i.altText) : [],
        mid: this.imageItemsMid ? this.imageItemsMid.map((i) => i.altText) : [],
      });

      // Emit event if needed
      this.$emit("button-click", this.langmode);
    },
    async onlyLoadAccepted() {
      const axios = require("axios");
      let data = JSON.stringify({
        token: localStorage.token,
      });
      let config = {
        method: "post",
        maxBodyLength: Infinity,
        url: process.env.VUE_APP_BACKEND_URL + "/v1/dashboard/sidebar-view",
        headers: {
          "X-CSRFToken": localStorage.getItem("csrf_token"),
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: data,
      };

      // API request - return the promise!
      return axios
        .request(config)
        .then((response) => {
          console.log(JSON.stringify(response.data));
          if (response.status === 200) {
            console.log("success");
            this.data_collection = response.data.data;

            // Debug the collected data
            console.log("Collected data: ");
            console.log(this.data_collection);

            localStorage.setItem("acceptedArrays", this.data_collection);
            var newItemsImageTop = [];
            var newimageItemsManageDevice = [];
            var newimageItemsMid = [];
            if (this.data_collection) {
              // Parse the diff arrays here
              console.log(this.data_collection);
              // Add accordingly
              if (this.data_collection.includes("Register Device")) {
                newItemsImageTop.push({
                  id: 1,
                  iconClass: "bx-user-check",
                  altText: "Register User",
                  route: "/enroll",
                });
              }
              // SIMULATION hub — only registered when the local-only src/sim/ folder is
              // present (gitignored; absent in staging/production). main.js sets this global.
              if (window.__CTRLYTICS_SIM_NAV__) {
                newItemsImageTop.push(window.__CTRLYTICS_SIM_NAV__);
              }
              if (this.data_collection.includes("Manage Devices")) {
                newimageItemsManageDevice.push({
                  id: 2,
                  iconClass: "bx-mobile",
                  altText: "Manage Devices",
                  route: "/device-list",
                });
              }
              if (this.data_collection.includes("Manage Accounts")) {
                newimageItemsMid.push({
                  id: 1,
                  iconClass: "bx-id-card",
                  altText: "Manage Accounts / PoS",
                  route: "/manage-user-admin",
                });
              }
              if (this.data_collection.includes("Manage Templates")) {
                if (this.data_collection.includes("credit_system")) {
                  newimageItemsMid.push({
                    id: 2,
                    iconClass: "bx-detail",
                    altText: "Manage Templates",
                    route: "/settings",
                  });
                } else {
                  newimageItemsMid.push({
                    id: 2,
                    iconClass: "bx-detail",
                    altText: "Manage Templates",
                    route: "/manage-templates",
                  });
                }
              }

              newimageItemsMid.push({
                id: 3,
                iconClass: "bx-help-circle",
                altText: "FAQ",
                route: "/faq",
              });
              if (this.data_collection.includes("Reports")) {
                newimageItemsMid.push({
                  id: 5,
                  iconClass: "bx-history",
                  altText: "Audit Trail",
                  route: "/reporting/audit-trail",
                });
                newimageItemsMid.push({
                  id: 6,
                  iconClass: "bx-briefcase-alt-2",
                  altText: "Reporting",
                  route: "/reporting-v2",
                });
              }
              // Put array data here
              this.imageItemsTop = newItemsImageTop;
              this.imageItemsManageDevice = newimageItemsManageDevice;
              this.imageItemsMid = newimageItemsMid;
              console.log("From LocalStorage data: ");
              console.log(this.data_collection);

              // Re-apply saved language if language data is loaded
              if (this.data && Object.keys(this.data).length > 0) {
                const savedLang = localStorage.locale || "eng";
                const langKey =
                  savedLang === "eng" ||
                  savedLang === "ENG" ||
                  savedLang === "english"
                    ? "ENG"
                    : savedLang === "bm" ||
                      savedLang === "BM" ||
                      savedLang === "bahasa"
                    ? "BM"
                    : savedLang === "th" ||
                      savedLang === "TH" ||
                      savedLang === "thai"
                    ? "th"
                    : "ENG";
                console.log("[Axios Success] Re-applying language:", langKey);
                this.langchange(langKey);
              }
            } else {
              // Error out here
              console.log("No restrictions found");
            }
          }
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
          this.tryagainWarning = true;
        });
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.isCollapsed = true;
      }
    },
  },
  async mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);

    const response = await fetch("/sidenav.json");
    const data = await response.json();
    await this.onlyLoadAccepted().then(() => {
      if (localStorage.token == undefined) {
        this.goToPage("/");
      }
      this.data = data;
      // Read from localStorage and normalize to uppercase for sidenav.json lookup
      const savedLang = localStorage.locale || "eng";
      const initialLang =
        savedLang === "eng" || savedLang === "ENG" || savedLang === "english"
          ? "ENG"
          : savedLang === "bm" || savedLang === "BM" || savedLang === "bahasa"
          ? "BM"
          : savedLang === "th" || savedLang === "TH" || savedLang === "thai"
          ? "th"
          : "ENG";
      this.langchange(initialLang);
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
};
</script>

<style scoped>
.sidebar {
  height: 100dvh;
  width: 250px;
  background-color: var(--primary-color); /* Yellow Theme */
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow-x: hidden;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px 10px;
  background-color: var(--primary-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.mobile-close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 28px;
  color: var(--text-main);
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 1002;
}

.mobile-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

/* Hamburger Animation */
.toggle-btn {
  width: 24px;
  height: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1001;
}

.toggle-btn .bar {
  width: 100%;
  height: 3px;
  background-color: var(--text-main);
  border-radius: 2px;
  transition: all 0.3s ease-in-out;
}

/* Active state (Sidebar Expanded/Open) = Show 'X' or 'Arrow left'?
       Collapsed = Hamburger (3 bars)
       Expanded = X (Close)

       So if !isCollapsed (is-active):
    */
.toggle-btn.is-active .bar:nth-child(1) {
  transform: translateY(8.5px) rotate(45deg);
}
.toggle-btn.is-active .bar:nth-child(2) {
  opacity: 0;
}
.toggle-btn.is-active .bar:nth-child(3) {
  transform: translateY(-8.5px) rotate(-45deg);
}

.sidebar.collapsed .toggle-btn {
  left: 20px; /* Keep centered in collapsed state */
}

.logo-container {
  margin-top: 20px; /* Space for toggle button */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.full-logo {
  width: 150px;
  height: auto;
  transition: all 0.3s ease;
}

.icon-logo {
  width: 35px;
  height: 35px;
  transition: all 0.3s ease;
}

/* Vue transition for logo swap */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  padding-bottom: 20px;
}

.menu-section {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-radius: var(--border-radius-md);
  transition: all 0.2s ease;
  color: var(--text-main);
  white-space: nowrap; /* Prevent text wrap during transition */
  position: relative; /* For dropdown */
}

.menu-item:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

.menu-item.active {
  background-color: var(
    --surface-color
  ); /* White for active state on yellow bg */
  color: var(--text-main);
  font-weight: 700;
  box-shadow: var(--shadow-sm);
}

.icon-image {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  object-fit: contain;
  flex-shrink: 0; /* Prevent icon shrinking */
  font-size: 24px; /* For font icons */
  display: flex; /* Center font icons */
  align-items: center;
  justify-content: center;
}

.sidebar.collapsed .icon-image {
  margin-right: 0;
}

.sidebar.collapsed .menu-item {
  justify-content: center;
  padding: 12px 0;
}

.image-label {
  font-family: var(--font-family-base);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.sidebar-footer {
  padding: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

/* Dropdown Styles */
.dropdown-arrow {
  margin-left: auto;
  font-size: 10px;
  transition: transform 0.2s;
}
.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.lang-dropdown {
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 100%;
  background-color: var(--surface-color);
  box-shadow: var(--shadow-xl);
  border-radius: var(--border-radius-md);
  padding: 5px 0;
  z-index: 1050;
  margin-bottom: 5px; /* Space above the button */
}

.sidebar.collapsed .lang-dropdown {
  width: 100%; /* Match sidebar width */
  left: 0;
  bottom: 100%;
  margin-bottom: 5px;
}

.sidebar.collapsed .lang-menu-item {
  position: relative;
}

.lang-option {
  padding: 10px 15px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
}

/* Center flags when collapsed */
.sidebar.collapsed .lang-option {
  justify-content: center;
  padding: 10px 0;
}

.lang-option:hover {
  background-color: var(--background-color);
}

.flag-mini {
  width: 20px;
  height: 15px;
  margin-right: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sidebar.collapsed .flag-mini {
  margin-right: 0;
}

.logout-item {
  color: #d32f2f;
}

.logout-item:hover {
  background-color: rgba(211, 47, 47, 0.1);
}

/* ===== PREMIUM MODAL STYLES ===== */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

.premium_modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 400px;
  background-color: var(--surface-color);
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.modal_content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.modal_icon_wrapper {
  margin-bottom: 10px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal_icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.modal_title {
  font-family: var(--font-family-header);
  font-size: 24px;
  font-weight: 800;
  color: var(--text-main);
  margin-bottom: 10px;
}

.modal_message {
  font-family: var(--font-family-base);
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 25px;
  max-width: 100%;
}

.modal_actions {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 15px; /* Added gap between buttons */
}

.btn_primary_large {
  background-color: var(--primary-color);
  color: var(--text-main);
  font-family: var(--font-family-header);
  font-weight: 700;
  font-size: 14px;
  border: none;
  border-radius: 12px;
  padding: 12px 24px; /* Adjusted padding */
  min-width: 120px; /* Minimum width */
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.btn_primary_large:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  filter: brightness(105%);
}

.btn_secondary_large {
  background-color: transparent;
  color: var(--text-secondary);
  border: 1px solid #ddd;
  font-family: var(--font-family-header);
  font-weight: 700;
  font-size: 14px;
  border-radius: 12px;
  padding: 12px 24px;
  min-width: 120px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn_secondary_large:hover {
  background-color: rgba(0, 0, 0, 0.05);
  border-color: #ccc;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 250px;
    z-index: 3000 !important;
    transform: translateX(0);
  }

  /* When collapsed on mobile, slide it out of view completely */
  .sidebar.collapsed {
    width: 250px; /* Maintain width */
    transform: translateX(-100%);
  }

  .sidebar-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 2000 !important;
  }
}
</style>
